import { FormsModule } from '@angular/forms';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { DateTimePipe, LicenseUsageTypeReport } from '@nimmsta/ng-commons-lib';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatePicker } from 'primeng/datepicker';
import { MultiSelect } from 'primeng/multiselect';
import { LicenseUsageReportAggregationService } from './license-usage-report.service';

@Component({
  selector: 'app-organization-license-usage-report',
  templateUrl: './organization-license-usage-report.component.html',
  imports: [FormsModule, DatePicker, MultiSelect, TranslatePipe, DateTimePipe],
})
export class OrganizationLicenseUsageReportComponent implements OnInit {
  @Input({ required: true }) organizationId!: string;
  @Output() configureEntitlements = new EventEmitter<void>();

  private reportService = inject(LicenseUsageReportAggregationService);
  private translate = inject(TranslateService);

  month = new Date().toISOString().slice(0, 7);
  monthPickerValue = this.toMonthDate(this.month);
  maxMonth = this.toMonthDate(this.month);
  selectedTypes: string[] = [];
  availableTypes: Array<{ label: string; value: string }> = [];
  reports: LicenseUsageTypeReport[] = [];
  availableMonths: string[] = [];
  failedSources: string[] = [];
  loading = false;
  monthError = false;

  ngOnInit(): void {
    this.loadMonthAvailability();
    this.reload();
  }

  reload(): void {
    this.monthError = !this.isValidMonth(this.month);
    if (this.monthError) {
      return;
    }

    this.loading = true;
    this.reportService
      .load(this.organizationId, this.month, this.selectedTypes)
      .subscribe({
        next: (response) => {
          this.availableTypes = response.availableTypes.map((type) => ({
            label: this.typeLabel(type),
            value: type,
          }));
          if (this.selectedTypes.length === 0) {
            this.selectedTypes = response.availableTypes;
          }
          this.reports = response.reports;
          this.failedSources = response.failures.map(
            (failure) => failure.source,
          );
          this.loading = false;
        },
        error: () => {
          this.reports = [];
          this.failedSources = [];
          this.loading = false;
        },
      });
  }

  onMonthSelect(value: Date | null): void {
    this.month = value ? this.toMonthString(value) : '';
    this.reload();
  }

  onTypesChange(): void {
    this.loadMonthAvailability();
    this.reload();
  }

  typeLabel(type: string): string {
    const key = `organizations.licenseUsage.types.${type}`;
    const translated = this.translate.instant(key);
    return translated === key
      ? type
          .toLowerCase()
          .split('_')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ')
      : translated;
  }

  availableMonthLabels(): string[] {
    return this.availableMonths.map((month) => {
      const value = this.toMonthDate(month);
      return `${String(value.getMonth() + 1).padStart(2, '0')}/${value.getFullYear()}`;
    });
  }

  private isValidMonth(month: string): boolean {
    return /^\d{4}-(0[1-9]|1[0-2])$/.test(month);
  }

  private toMonthDate(month: string): Date {
    const [year, selectedMonth] = month.split('-').map(Number);
    return new Date(year, selectedMonth - 1, 1);
  }

  private toMonthString(value: Date): string {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
  }

  private loadMonthAvailability(): void {
    this.reportService
      .loadMonthAvailability(this.organizationId, this.selectedTypes)
      .subscribe({
        next: (response) => {
          this.availableMonths = [
            ...new Set(Object.values(response.monthsByType).flat()),
          ].sort();
        },
        error: () => {
          this.availableMonths = [];
        },
      });
  }
}
